/**
 * premiumButtons.js
 *
 * Runtime tagger for the premium layered-lighting button treatment.
 * Instead of enumerating every brand colour class, this scans real
 * button-like elements, decides if they are "filled" (opaque/saturated
 * background or a gradient), and tags them with `.bp-btn-premium` plus a
 * per-button `--bp-btn-glow` derived from the button's OWN colour. The
 * actual gradient + 4-layer shadow + press/hover/disabled states live in
 * premiumButtons.css, so brand colours are always preserved.
 *
 * Covers: Tailwind arbitrary/named colours, custom classes, inline styles
 * and dynamically-added buttons (modals, dropdowns) via a MutationObserver.
 */

const FLAG = 'data-bp-btn';

const parseColor = (str) => {
  if (!str) return null;
  const m = str.match(/rgba?\(([^)]+)\)/i);
  if (!m) return null;
  const parts = m[1].split(',').map((n) => parseFloat(n));
  const [r, g, b] = parts;
  const a = parts.length > 3 ? parts[3] : 1;
  return [r, g, b, a];
};

// relative luminance (0 dark .. 1 light)
const luminance = ([r, g, b]) => (0.2126 * r + 0.7152 * g + 0.0722 * b) / 255;

const firstGradientColor = (bgImage) => {
  const m = bgImage.match(/rgba?\(([^)]+)\)/i);
  if (m) {
    const [r, g, b] = m[1].split(',').map((n) => parseFloat(n));
    return `${Math.round(r)},${Math.round(g)},${Math.round(b)}`;
  }
  return '29,142,254';
};

const process = (el) => {
  if (el.getAttribute(FLAG)) return;

  const tag = el.tagName;
  const isButtonEl = tag === 'BUTTON' || el.getAttribute('role') === 'button';
  const isAnchor = tag === 'A';
  if (!isButtonEl && !isAnchor) { el.setAttribute(FLAG, 'skip'); return; }

  const cs = getComputedStyle(el);
  const hasGradient = /gradient/i.test(cs.backgroundImage);
  const bg = parseColor(cs.backgroundColor);
  const opaque = bg && bg[3] >= 0.6;
  const backdrop = cs.backdropFilter || cs.webkitBackdropFilter || '';
  const hasBlur = backdrop && backdrop !== 'none';

  // Frosted-glass buttons (translucent bg + backdrop blur): keep the glass
  // look, add a subtle highlight + depth only.
  if (!hasGradient && !opaque) {
    if (hasBlur && bg && bg[3] > 0.02) {
      el.classList.add('bp-btn-premium', 'bp-btn-glass');
      el.style.setProperty('--bp-btn-glow', '255,255,255');
      el.setAttribute(FLAG, '1');
      return;
    }
    el.setAttribute(FLAG, 'skip');
    return;
  }

  // For anchors, be conservative so we never hit content cards:
  // must look like a pill/button (rounded, not huge, no block media inside).
  if (isAnchor && !isButtonEl) {
    const radius = parseFloat(cs.borderRadius) || 0;
    const h = el.offsetHeight;
    const hasBlockChild = !!el.querySelector('img, h1, h2, h3, h4, p');
    const saturated = hasGradient || (bg && luminance(bg) < 0.9);
    if (!(radius > 0 && h > 0 && h <= 96 && !hasBlockChild && saturated)) {
      el.setAttribute(FLAG, 'skip');
      return;
    }
  }

  let glow;
  let light = false;
  if (hasGradient) {
    glow = firstGradientColor(cs.backgroundImage);
    el.classList.add('bp-btn-gradient');
  } else {
    glow = `${Math.round(bg[0])},${Math.round(bg[1])},${Math.round(bg[2])}`;
    light = luminance(bg) > 0.72;
  }

  el.style.setProperty('--bp-btn-glow', glow);
  el.classList.add('bp-btn-premium');
  if (light) el.classList.add('bp-btn-light');
  el.setAttribute(FLAG, '1');
};

let scheduled = false;
const scan = () => {
  scheduled = false;
  const nodes = document.querySelectorAll('button, a, [role="button"]');
  for (let i = 0; i < nodes.length; i++) process(nodes[i]);
};
const schedule = () => {
  if (scheduled) return;
  scheduled = true;
  (window.requestAnimationFrame || window.setTimeout)(scan, 50);
};

export const initPremiumButtons = () => {
  if (typeof document === 'undefined' || window.__bpBtnInit) return;
  window.__bpBtnInit = true;

  const start = () => {
    scan();
    const mo = new MutationObserver(() => schedule());
    mo.observe(document.body, { childList: true, subtree: true });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
};
